import { formatCents, type InventoryItem } from '@lbhl/shared';
import type { Tier } from '../lib/tiers.js';

export type EngineeringCounts = Record<string, Record<string, string>>;

interface Props {
  tiers: Tier[];
  items: InventoryItem[];
  counts: EngineeringCounts;
  onChange(packageCode: string, sku: string, value: string): void;
}

function countOf(counts: EngineeringCounts, packageCode: string, sku: string): number {
  const raw = Number(counts[packageCode]?.[sku] ?? '');
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
}

/**
 * How many of each consumable the job needs — the ones nothing can work out from a
 * measurement.
 *
 * Footage tells you the bulbs, the socket wire and the clips. It tells you nothing
 * about how many plugs, bridge plugs, termination caps or coax adapters the
 * installer will use, because that depends on how the circuit is actually run. Until
 * now the cost model guessed at three of these and silently omitted five.
 *
 * Laid out as a matrix — items down, tiers across — because every tier offers the
 * same items and reading the progression along one line is the point. Each box is
 * what that tier ADDS, exactly like roofline footage, so a higher tier genuinely
 * carries more.
 *
 * Internal: none of this moves a price or reaches a customer.
 */
export function Engineering({ tiers, items, counts, onChange }: Props) {
  if (items.length === 0) {
    return (
      <section className="card">
        <h2>Engineering</h2>
        <div className="note dim">
          No hand-counted items are configured in this pricing version.
        </div>
      </section>
    );
  }

  return (
    <section className="card">
      <h2>Engineering — internal only, never shown to the customer</h2>
      <div className="note">
        Counts for the parts no measurement implies. Each box is what that package
        <strong> adds</strong>, so the Job column is the full count. These feed the
        internal cost figure and change <strong>no</strong> price.
      </div>

      <div className="engmatrix">
        <div className="engrow enghead">
          <span className="label" />
          {tiers.map(({ pkg }) => (
            <span className="num" key={pkg.code} title={pkg.name}>
              {pkg.name.slice(0, 3)}
            </span>
          ))}
          <span className="num">Job</span>
        </div>

        {items.map((item) => {
          const total = tiers.reduce(
            (sum, { pkg }) => sum + countOf(counts, pkg.code, item.sku),
            0,
          );
          return (
            <div className="engrow" key={item.sku}>
              <span className="label">
                {item.name}
                <br />
                <span className="unit">
                  {formatCents(item.unitCostCents)} / {item.uom}
                </span>
              </span>

              {tiers.map(({ pkg }) => (
                <input
                  key={pkg.code}
                  type="number"
                  inputMode="numeric"
                  min="0"
                  step="1"
                  aria-label={`${item.name}, added by ${pkg.name}`}
                  value={counts[pkg.code]?.[item.sku] ?? ''}
                  onChange={(e) => onChange(pkg.code, item.sku, e.target.value)}
                />
              ))}

              <span className={total > 0 ? 'num strong' : 'num dim'}>{total || '—'}</span>
            </div>
          );
        })}
      </div>

      <div className="note dim">
        Unit costs come from the pricing version and are edited in the Pricing tab.
        Leaving a box empty costs nothing rather than guessing.
      </div>
    </section>
  );
}
